/**
 * Hooks React Query du domaine client.
 *
 * Adaptateurs minces : cache et notifications, rien d'autre. Le calcul de
 * l'encours, l'interdiction d'archiver un débiteur et les droits par rôle
 * vivent dans `@dker/core` et valent identiquement pour les trois portes.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DomainError } from "@dker/core";
import type { Client, ClientDetail, SaveClientInput } from "@dker/core";
import { clientServices, serviceDeps, useExecutionContext } from "@/services";
import { useTenantOptional } from "@/hooks/useTenant";
import { useUiStore } from "@/stores/uiStore";

export type { Client, ClientDetail };

function toMessage(error: unknown): string {
  if (error instanceof DomainError) return error.message;
  return "Une erreur inattendue est survenue. Réessayez dans un instant.";
}

export function useClients(search?: string) {
  const tenant = useTenantOptional();
  const makeContext = useExecutionContext();

  return useQuery({
    queryKey: ["clients", tenant?.id, search ?? ""],
    enabled: !!tenant,
    queryFn: () => clientServices.listerClients(makeContext(), { search }, serviceDeps),
  });
}

export function useClient(clientId: string | undefined) {
  const makeContext = useExecutionContext();

  return useQuery({
    queryKey: ["client", clientId],
    enabled: !!clientId,
    queryFn: () =>
      clientServices.lireClient(makeContext(), { clientId: clientId! }, serviceDeps),
  });
}

export function useSaveClient() {
  const qc = useQueryClient();
  const tenant = useTenantOptional();
  const makeContext = useExecutionContext();
  const addToast = useUiStore((s) => s.addToast);

  return useMutation({
    mutationFn: (input: SaveClientInput) =>
      clientServices.enregistrerClient(makeContext(), input, serviceDeps),

    onSuccess: (client, input) => {
      void qc.invalidateQueries({ queryKey: ["clients", tenant?.id] });
      void qc.invalidateQueries({ queryKey: ["client", client.id] });
      addToast({
        type: "success",
        title: input.clientId ? "Client modifié" : "Client créé",
      });
    },

    onError: (error) =>
      addToast({
        type: "error",
        title: "Enregistrement impossible",
        message: toMessage(error),
      }),
  });
}

export function useArchiveClient() {
  const qc = useQueryClient();
  const tenant = useTenantOptional();
  const makeContext = useExecutionContext();
  const addToast = useUiStore((s) => s.addToast);

  return useMutation({
    mutationFn: (clientId: string) =>
      clientServices.archiverClient(makeContext(), { clientId }, serviceDeps),

    onSuccess: ({ clientId }) => {
      void qc.invalidateQueries({ queryKey: ["clients", tenant?.id] });
      void qc.invalidateQueries({ queryKey: ["client", clientId] });
      addToast({ type: "success", title: "Client archivé" });
    },

    onError: (error) =>
      addToast({
        type: "error",
        title: "Archivage impossible",
        message: toMessage(error),
      }),
  });
}
